import { useEffect, useState } from "react";
import { useAlarm } from "@/contexts/AlarmContext";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { X, Alarm as AlarmIcon, Broadcast, Plus, Trash, Pencil } from "@phosphor-icons/react";
import { toast } from "sonner";
import { TEST_IDS } from "@/constants/testIds";

const DAY_LABELS = ["M", "T", "W", "T", "F", "S", "S"];

function describeAlarm(a) {
  if (a.recurring === "daily") return "Every day";
  if (a.recurring === "weekdays") {
    const sel = (a.days || []).map((on, i) => on ? DAY_LABELS[i] : null).filter(Boolean);
    return sel.length === 0 ? "(no days)" : sel.join(" · ");
  }
  return "Once";
}

export default function AlarmModal({ onClose, initialEditId = null }) {
  const { alarms, upsertAlarm, removeAlarm, toggleAlarm } = useAlarm();
  const { user } = useAuth();
  const [favRadios, setFavRadios] = useState([]);
  const [loading, setLoading] = useState(true);

  // Editor state
  const [editing, setEditing] = useState(null); // null = list view; object = create/edit

  useEffect(() => {
    if (!user) { setLoading(false); return; }
    api.get("/favorites/radios")
      .then(r => setFavRadios(r.data || []))
      .catch(() => { /* ignore */ })
      .finally(() => setLoading(false));
  }, [user]);

  // Open editor for a given alarm id when modal mounts. If no initialEditId and
  // there are no alarms yet, auto-enter the create flow for a smoother UX.
  useEffect(() => {
    if (initialEditId) {
      const a = alarms.find(x => x.id === initialEditId);
      if (a) {
        setEditing({
          id: a.id, time: a.time, station: a.station,
          recurring: a.recurring || "once",
          days: a.days || [false,false,false,false,false,false,false],
          label: a.label || "Wake",
        });
      }
    } else if (alarms.length === 0 && favRadios.length > 0) {
      setEditing({
        id: null, time: "07:30", station: favRadios[0],
        recurring: "once", days: [false,false,false,false,false,false,false],
        label: "Wake",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialEditId, favRadios.length]);

  const startCreate = () => {
    setEditing({
      id: null, time: "07:30", station: favRadios[0] || null,
      recurring: "once", days: [false,false,false,false,false,false,false],
      label: "Wake",
    });
  };

  const startEdit = (a) => {
    setEditing({
      id: a.id, time: a.time,
      station: favRadios.find(s => s.stationuuid === a.station?.stationuuid) || a.station,
      recurring: a.recurring || "once",
      days: a.days || [false,false,false,false,false,false,false],
      label: a.label || "Wake",
    });
  };

  const save = () => {
    if (!editing.station) { toast.error("Pick a station"); return; }
    if (editing.recurring === "weekdays" && !editing.days.some(Boolean)) {
      toast.error("Select at least one day"); return;
    }
    upsertAlarm({ ...editing, enabled: true });
    toast.success(editing.id ? "Alarm updated" : `Alarm set for ${editing.time}`);
    setEditing(null);
    onClose?.();
  };

  const toggleDay = (i) => {
    const days = [...editing.days];
    days[i] = !days[i];
    setEditing({ ...editing, days });
  };

  return (
    <div
      data-testid="alarm-modal"
      className="fixed inset-0 z-[70] bg-[#1F2924]/80 flex items-end sm:items-center justify-center p-0 sm:p-4"
      onClick={onClose}
    >
      <div
        className="bg-[#F4F1EA] border-t-2 sm:border-2 border-[#1F2924] shadow-tactile w-full sm:max-w-md max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-5 sm:p-6">
          <div className="flex items-start justify-between mb-5">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D35230] mb-1">Wake</div>
              <h3 className="font-serif-display text-2xl sm:text-3xl leading-none">
                {editing ? (editing.id ? "Edit alarm" : "New alarm") : "Alarms"}
              </h3>
            </div>
            <button onClick={onClose} className="w-9 h-9 border border-[#1F2924]/30 hover:border-[#D35230] flex items-center justify-center" aria-label="Close">
              <X size={16} />
            </button>
          </div>

          {!user ? (
            <p className="font-sans text-[#1F2924]/70">Sign in to save alarms.</p>
          ) : loading ? (
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#D35230]">Loading…</p>
          ) : favRadios.length === 0 ? (
            <div className="text-center py-6">
              <Broadcast size={36} color="#D35230" weight="duotone" className="mx-auto mb-3" />
              <p className="font-serif-display text-xl mb-2">No favorite stations yet</p>
              <p className="font-sans text-sm text-[#4A5A50]">Save at least one station to use it as an alarm.</p>
            </div>
          ) : editing ? (
            <div className="space-y-5">
              {/* Label */}
              <div>
                <label className="block font-mono text-[10px] uppercase tracking-[0.3em] text-[#4A5A50] mb-2">Label</label>
                <input
                  data-testid="alarm-label-input"
                  value={editing.label}
                  onChange={(e) => setEditing({ ...editing, label: e.target.value })}
                  placeholder="Wake, Workout, Meditation…"
                  className="w-full px-3 py-2.5 bg-[#EAE4D3] border-2 border-[#1F2924]/30 focus:border-[#D35230] outline-none font-sans"
                />
              </div>

              {/* Time */}
              <div>
                <label className="block font-mono text-[10px] uppercase tracking-[0.3em] text-[#4A5A50] mb-2">Time</label>
                <input
                  data-testid="alarm-time-input"
                  type="time"
                  value={editing.time}
                  onChange={(e) => setEditing({ ...editing, time: e.target.value })}
                  className="w-full px-3 py-2.5 bg-[#EAE4D3] border-2 border-[#1F2924]/30 focus:border-[#D35230] outline-none font-mono text-2xl tracking-wider"
                />
              </div>

              {/* Station */}
              <div>
                <label className="block font-mono text-[10px] uppercase tracking-[0.3em] text-[#4A5A50] mb-2">Station</label>
                <select
                  data-testid="alarm-station-select"
                  value={editing.station?.stationuuid || ""}
                  onChange={(e) => setEditing({ ...editing, station: favRadios.find(s => s.stationuuid === e.target.value) })}
                  className="w-full px-3 py-2.5 bg-[#EAE4D3] border-2 border-[#1F2924]/30 focus:border-[#D35230] outline-none font-sans"
                >
                  {favRadios.map(s => (
                    <option key={s.stationuuid} value={s.stationuuid}>{s.name} {s.country ? `· ${s.country}` : ""}</option>
                  ))}
                </select>
              </div>

              {/* Recurring */}
              <div>
                <label className="block font-mono text-[10px] uppercase tracking-[0.3em] text-[#4A5A50] mb-2">Repeat</label>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { id: "once", label: "Once" },
                    { id: "daily", label: "Daily" },
                    { id: "weekdays", label: "Days" },
                  ].map(opt => (
                    <button
                      key={opt.id}
                      data-testid={`alarm-recurring-${opt.id}`}
                      onClick={() => setEditing({ ...editing, recurring: opt.id })}
                      className={`py-2.5 border-2 font-mono text-[10px] sm:text-xs uppercase tracking-[0.2em] ${
                        editing.recurring === opt.id
                          ? "bg-[#1F2924] text-[#F4F1EA] border-[#1F2924]"
                          : "border-[#1F2924]/30 hover:border-[#1F2924]"
                      }`}
                    >{opt.label}</button>
                  ))}
                </div>
              </div>

              {/* Day picker (when 'weekdays') */}
              {editing.recurring === "weekdays" && (
                <div>
                  <label className="block font-mono text-[10px] uppercase tracking-[0.3em] text-[#4A5A50] mb-2">Pick days</label>
                  <div className="grid grid-cols-7 gap-1.5">
                    {DAY_LABELS.map((d, i) => (
                      <button
                        key={i}
                        data-testid={`alarm-day-${i}`}
                        onClick={() => toggleDay(i)}
                        className={`aspect-square border-2 font-mono text-sm ${
                          editing.days[i]
                            ? "bg-[#D35230] border-[#D35230] text-[#F4F1EA]"
                            : "border-[#1F2924]/30 hover:border-[#1F2924] text-[#1F2924]"
                        }`}
                      >{d}</button>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex items-center gap-3 pt-2">
                <button
                  data-testid={TEST_IDS.player.alarmSave}
                  onClick={save}
                  className="flex-1 py-3 border-2 border-[#1F2924] bg-[#1F2924] text-[#F4F1EA] hover:bg-[#D35230] hover:border-[#D35230] font-mono text-xs uppercase tracking-[0.25em] flex items-center justify-center gap-2"
                >
                  <AlarmIcon size={14} weight="fill" /> {editing.id ? "Update" : "Set alarm"}
                </button>
                <button
                  data-testid="alarm-cancel-edit"
                  onClick={() => setEditing(null)}
                  className="px-4 py-3 border-2 border-[#1F2924]/30 hover:border-[#1F2924] font-mono text-xs uppercase tracking-[0.2em]"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div>
              <button
                data-testid="alarm-add-new"
                onClick={startCreate}
                className="w-full mb-4 py-3 border-2 border-dashed border-[#1F2924]/40 hover:border-[#D35230] hover:text-[#D35230] font-mono text-xs uppercase tracking-[0.2em] flex items-center justify-center gap-2"
              >
                <Plus size={14} /> Add alarm
              </button>

              {alarms.length === 0 ? (
                <p className="text-center font-serif-display text-lg text-[#4A5A50] py-6">No alarms yet.</p>
              ) : (
                <div className="space-y-2">
                  {alarms.map((a) => (
                    <div key={a.id} data-testid="alarm-item" className="bg-[#EAE4D3] border border-[#1F2924]/20 p-3 flex items-center gap-3">
                      <button
                        data-testid={`alarm-toggle-${a.id}`}
                        onClick={() => toggleAlarm(a.id)}
                        aria-label={a.enabled ? "Disable" : "Enable"}
                        className={`relative w-10 h-6 border-2 transition-colors shrink-0 ${a.enabled ? "bg-[#D35230] border-[#D35230]" : "border-[#1F2924]/40 bg-transparent"}`}
                      >
                        <span className={`absolute top-0 ${a.enabled ? "right-0" : "left-0"} w-4 h-[18px] bg-[#F4F1EA] border border-[#1F2924]/30 transition-all`} />
                      </button>
                      <div className="flex-1 min-w-0">
                        <div className="font-mono text-2xl tracking-wider text-[#1F2924] leading-none">{a.time}</div>
                        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#D35230] mt-1">{a.label}</div>
                        <div className="font-sans text-xs text-[#4A5A50] mt-0.5 truncate">
                          {describeAlarm(a)} · {a.station?.name}
                        </div>
                      </div>
                      <button
                        data-testid={`alarm-edit-${a.id}`}
                        onClick={() => startEdit(a)}
                        aria-label="Edit"
                        className="w-8 h-8 border border-[#1F2924]/30 hover:border-[#1F2924] flex items-center justify-center"
                      ><Pencil size={14} /></button>
                      <button
                        data-testid={`alarm-delete-${a.id}`}
                        onClick={() => removeAlarm(a.id)}
                        aria-label="Delete"
                        className="w-8 h-8 border border-[#1F2924]/30 hover:border-[#D35230] flex items-center justify-center"
                      ><Trash size={14} /></button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-[#4A5A50] mt-5 leading-relaxed">
            Note: Alarms fire only while Recho Music is open in this browser/tab.
          </p>
        </div>
      </div>
    </div>
  );
}
